import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiClient from '../../utils/api';

const Container = styled.div`
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #212529;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Stats = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: 768px) {
    width: 100%;
    gap: 6px;
  }
`;

const StatCard = styled.div<{ variant?: string }>`
  padding: 12px 20px;
  background: ${props => props.variant === 'primary' ? '#212529' : '#f8f9fa'};
  border: 1px solid #dee2e6;
  border-radius: 12px;
  color: ${props => props.variant === 'primary' ? 'white' : '#212529'};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 11px;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Th = styled.th`
  background: #f8f9fa;
  padding: 14px 12px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  
  @media (max-width: 768px) {
    padding: 10px 2px;
    font-size: 11px;
  }
`;

const Td = styled.td`
  padding: 14px 12px;
  font-size: 14px;
  color: #212529;
  border-bottom: 1px solid #dee2e6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 10px 2px;
    font-size: 12px;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => 
    props.status === 'R' ? '#212529' :
    props.status === 'Y' ? '#f8f9fa' :
    props.status === 'N' ? '#6c757d' : '#e9ecef'
  };
  color: ${props => 
    props.status === 'R' ? 'white' :
    props.status === 'Y' ? '#212529' :
    props.status === 'N' ? 'white' : '#495057'
  };
  border: 1px solid ${props => 
    props.status === 'Y' ? '#dee2e6' : 'transparent'
  };
  
  @media (max-width: 768px) {
    font-size: 10px;
    padding: 2px 6px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
  
  @media (max-width: 768px) {
    gap: 3px;
  }
`;

const ActionButton = styled.button<{ variant: 'approve' | 'reject' }>`
  padding: 6px 14px;
  border: 1px solid;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.variant === 'approve' ? '#212529' : 'white'};
  color: ${props => props.variant === 'approve' ? 'white' : '#212529'};
  border-color: #212529;
  
  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 10px;
  }
`;

const EmptyMessage = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 460px;
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);

  @media (max-width: 768px) {
    max-width: none;
    border-radius: 20px 20px 0 0;
    padding: 20px;
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 700;
  color: #212529;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ModalText = styled.div`
  margin-bottom: 20px;
  color: #495057;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'outline' }>`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #212529;
  border-radius: 8px;
  background: ${props => props.variant === 'primary' ? '#212529' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#212529'};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ModalError = styled.div`
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff5f5;
  color: #842029;
  font-size: 13px;
`;

interface Application {
  user_no: number;
  user_id: string;
  name: string;
  gender: string;
  phone: string;
  address: string;
  birth_year: string;
  election_status: string;
  status: string;
  created_at: string;
}

const AdminMemberApplications: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('R'); // 대기 탭이 디폴트
  const [approveTarget, setApproveTarget] = useState<Application | null>(null);
  const [smsTarget, setSmsTarget] = useState<Application | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [modalError, setModalError] = useState('');

  // 가입신청 목록 조회
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/members/applications');
      if (response.data.success) {
        setApplications(response.data.data);
      }
    } catch (error) {
      console.error('가입신청 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const buildSmsLink = (member: Application) => {
    const smsMessage = `${member.name}님 가입이 승인되었습니다.
회원가입을 꼭 진행해 주세요.
회원가입 링크:
https://www.hb-volleyball.co.kr/create-account
해당 링크에서 회원가입 진행해주세요.

📌 월 회비 : 2만원

📌 가입비 : 8만원 (회비 + 유니폼비 포함)

✔️ 매월 10일까지 납부

✔️ 1년 또는 분기 일괄납부 가능

✔️ 납부 시 입금명 : '${member.name}' 

✔️ 계좌 : 카카오뱅크 79423097529  추도빈 

회비 납부 후 

H_B 톡방 아래 링크로 들어와 주세요~

https://open.kakao.com/o/gcXS8LZh

비밀번호 hb231105
`;

    const phoneNumber = member.phone.replace(/-/g, '');
    return `sms:${phoneNumber}?body=${encodeURIComponent(smsMessage)}`;
  };

  const handleApprove = async () => {
    if (!approveTarget) return;

    try {
      setIsApproving(true);
      setModalError('');
      const response = await apiClient.put(`/members/applications/${approveTarget.user_no}/approve`);
      if (response.data.success) {
        const approvedMember = approveTarget;
        setApproveTarget(null);
        await fetchApplications();

        if (approvedMember.phone) {
          setSmsTarget(approvedMember);
        }
      }
    } catch (error: any) {
      console.error('승인 실패:', error);
      setModalError(error.response?.data?.detail || '승인에 실패했습니다');
    } finally {
      setIsApproving(false);
    }
  };

  const handleSmsSend = () => {
    if (!smsTarget) return;
    window.location.href = buildSmsLink(smsTarget);
    setSmsTarget(null);
  };

  const handleReject = async (user_no: number) => {
    if (!window.confirm('이 신청을 거절하시겠습니까?')) return;
    
    try {
      const response = await apiClient.put(`/members/applications/${user_no}/reject`);
      if (response.data.success) {
        window.alert('거절되었습니다');
        fetchApplications(); // 목록 새로고침
      }
    } catch (error: any) {
      console.error('거절 실패:', error);
      window.alert(error.response?.data?.detail || '거절에 실패했습니다');
    }
  };

  const formatBirthYear = (birthYear: string): string => {
    if (birthYear.length >= 4) {
      return `${birthYear.slice(0, 2)}년생`;
    }
    return birthYear;
  };

  const getStatusText = (status: string): string => {
    switch(status) {
      case 'R': return '대기';
      case 'Y': return '승인';
      case 'N': return '거절';
      default: return status;
    }
  };

  const pendingCount = applications.filter(app => app.status === 'R').length;
  const approvedCount = applications.filter(app => app.status === 'Y').length;
  const rejectedCount = applications.filter(app => app.status === 'N').length;

  // 필터링된 신청 목록
  const filteredApplications = applications.filter(app => {
    if (filterStatus === '전체') return true;
    return app.status === filterStatus;
  });

  return (
    <DashboardLayout title="신규가입 관리">
      <Container>
        <Header>
          <Title>신규가입 관리</Title>
          <Stats>
            <StatCard 
              variant={filterStatus === 'R' ? 'primary' : undefined}
              onClick={() => setFilterStatus('R')}
              style={{ cursor: 'pointer' }}
            >
              대기 {pendingCount}
            </StatCard>
            <StatCard 
              variant={filterStatus === 'Y' ? 'primary' : undefined}
              onClick={() => setFilterStatus('Y')}
              style={{ cursor: 'pointer' }}
            >
              승인 {approvedCount}
            </StatCard>
            <StatCard 
              variant={filterStatus === 'N' ? 'primary' : undefined}
              onClick={() => setFilterStatus('N')}
              style={{ cursor: 'pointer' }}
            >
              거절 {rejectedCount}
            </StatCard>
          </Stats>
        </Header>

        <TableContainer>
          {loading ? (
            <EmptyMessage>로딩 중...</EmptyMessage>
          ) : filteredApplications.length === 0 ? (
            <EmptyMessage>
              {filterStatus === 'R' ? '대기 중인 신규 가입신청이 없습니다' :
               filterStatus === 'Y' ? '승인된 신규 가입신청이 없습니다' :
               filterStatus === 'N' ? '거절된 신규 가입신청이 없습니다' :
               '신규 가입신청이 없습니다'}
            </EmptyMessage>
          ) : (
            <Table>
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr>
                  <Th>이름</Th>
                  <Th>성별</Th>
                  <Th>생년월일</Th>
                  <Th>선출여부</Th>
                  <Th>상태</Th>
                  <Th>승인</Th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((app) => (
                  <tr key={app.user_no}>
                    <Td>{app.name}</Td>
                    <Td>{app.gender}</Td>
                    <Td>{formatBirthYear(app.birth_year)}</Td>
                    <Td>{app.election_status}</Td>
                    <Td>
                      <StatusBadge status={app.status}>
                        {getStatusText(app.status)}
                      </StatusBadge>
                    </Td>
                    <Td>
                      {app.status === 'R' && (
                        <ActionButtons>
                          <ActionButton 
                            variant="approve"
                            onClick={() => {
                              setModalError('');
                              setApproveTarget(app);
                            }}
                          >
                            O
                          </ActionButton>
                          <ActionButton 
                            variant="reject"
                            onClick={() => handleReject(app.user_no)}
                          >
                            X
                          </ActionButton>
                        </ActionButtons>
                      )}
                      {app.status === 'Y' && <span style={{ color: '#212529', fontSize: '13px' }}>✓ 승인됨</span>}
                      {app.status === 'N' && <span style={{ color: '#6c757d', fontSize: '13px' }}>✕ 거절됨</span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableContainer>
        {approveTarget && (
          <ModalOverlay onClick={() => !isApproving && setApproveTarget(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>가입 신청 승인</ModalTitle>
              <ModalText>
                {approveTarget.name}님의 가입 신청을 승인할까요?
                <br />
                승인 후 회원가입 안내 SMS를 보낼 수 있습니다.
              </ModalText>
              {modalError && <ModalError>{modalError}</ModalError>}
              <ModalActions>
                <ModalButton
                  variant="primary"
                  onClick={handleApprove}
                  disabled={isApproving}
                >
                  {isApproving ? '승인 중...' : '승인'}
                </ModalButton>
                <ModalButton
                  type="button"
                  onClick={() => setApproveTarget(null)}
                  disabled={isApproving}
                >
                  취소
                </ModalButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        {smsTarget && (
          <ModalOverlay onClick={() => setSmsTarget(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>승인 완료</ModalTitle>
              <ModalText>
                {smsTarget.name}님 가입 신청이 승인되었습니다.
                <br />
                안내 SMS를 보내시겠습니까?
              </ModalText>
              <ModalActions>
                <ModalButton variant="primary" onClick={handleSmsSend}>
                  SMS 보내기
                </ModalButton>
                <ModalButton type="button" onClick={() => setSmsTarget(null)}>
                  닫기
                </ModalButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default AdminMemberApplications;
